import Cropper from "react-easy-crop";
import { useState, useCallback } from "react";
import getCroppedImg from "../../utils/cropImage.tsx";

interface ProductModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  newProduct: {
    name: string;
    description: string;
    brand: string;
    category: string;
    price: number;
    stock: number;
    technicalDetails: string;
    image: File | null;
  };
  setNewProduct: React.Dispatch<React.SetStateAction<{
    name: string;
    description: string;
    brand: string;
    category: string;
    price: number;
    stock: number;
    technicalDetails: string;
    image: File | null;
  }>>;
  isEdit?: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
  show,
  onClose,
  onSubmit,
  newProduct,
  setNewProduct,
  isEdit
}) => {

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [cropping, setCropping] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.readAsDataURL(e.target.files[0]);
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
    }
  };

  const handleCropAndSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setCropping(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "cropped.jpg", { type: "image/jpeg" });
      setNewProduct((prev) => ({ ...prev, image: croppedFile }));
      setCropping(false);
      // Optionally, update imageSrc to show the cropped image preview
      const previewUrl = URL.createObjectURL(croppedBlob);
      setImageSrc(previewUrl);
    } catch (e) {
      setCropping(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 relative">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          ✕
        </button>
        <h2 className="text-xl font-bold mb-4">{isEdit ? "Ürün Düzenle" : "Ürün Ekle"}</h2>
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <label className="font-medium w-40">Adı</label>
          <input
            type="text"
            name="name"
            value={newProduct.name}
            onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
            required
          />
          <label className="font-medium w-40">Açıklama</label>
          <input
            type="text"
            name="description"
            value={newProduct.description}
            onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
          />
          <label className="font-medium w-40">Marka</label>
          <input
            type="text"
            name="brand"
            value={newProduct.brand}
            onChange={e => setNewProduct({ ...newProduct, brand: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
            required
          />
          <label className="font-medium w-40">Kategori</label>
          <input
            type="text"
            name="category"
            value={newProduct.category}
            onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
            required
          />
          <label className="font-medium w-40">Fiyat</label>
          <input
            type="number"
            name="price"
            value={newProduct.price}
            onChange={e => setNewProduct({ ...newProduct, price: Number(e.target.value) })}
            className="border rounded px-2 py-1 flex-1"
            min={0}
            required
          />
          <label className="font-medium w-40">Stok</label>
          <input
            type="number"
            name="stock"
            value={newProduct.stock}
            onChange={e => setNewProduct({ ...newProduct, stock: Number(e.target.value) })}
            className="border rounded px-2 py-1 flex-1"
            min={0}
            required
          />
          <label className="font-medium w-40">Özellikler</label>
          <textarea
            name="technicalDetails"
            value={newProduct.technicalDetails || ""}
            onChange={e => setNewProduct({ ...newProduct, technicalDetails: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
            rows={4}
            placeholder={`Her satıra bir özellik yazın:\nÇap: 12 cm\nMalzeme: Melamin\nRenk: Beyaz\nKırılmaz: Evet`}
          />
          <label className="font-medium w-40">Resim</label>
          <input type="file" accept="image/*" onChange={handleImageChange} />

          {imageSrc && (
            <>
              <div className="relative w-64 h-64 mb-2">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={4 / 3}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <button
                type="button"
                className="mb-2 bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                onClick={handleCropAndSave}
                disabled={cropping}
              >
                {cropping ? "Kırpılıyor..." : "Kırp ve Kullan"}
              </button>
            </>
          )}
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {isEdit ? "Kaydet" : "Ekle"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;